import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../css/content-page.scss";
import "../css/contactus.scss";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimerRef = useRef(null);
  const successTimerRef = useRef(null);
  const successRef = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(submitTimerRef.current);
      window.clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((previous) => {
        const nextErrors = { ...previous };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!formData.subject) {
      nextErrors.subject = "Select a subject";
    }

    if (!formData.message.trim()) {
      nextErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Message must be at least 10 characters";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    const formErrors = validateForm();
    setShowSuccess(false);

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    window.clearTimeout(submitTimerRef.current);
    window.clearTimeout(successTimerRef.current);

    submitTimerRef.current = window.setTimeout(() => {
      setShowSuccess(true);
      setFormData(emptyForm);
      setIsSubmitting(false);
      scrollToTop();

      window.requestAnimationFrame(() => {
        successRef.current?.focus();
      });

      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }, 1000);
  };

  return (
    <main className="content-page contact-page">
      <div className="content-page__shell content-page__shell--single">
        <article
          className="content-page__article content-page__article--wide"
          aria-labelledby="contact-title"
        >
          <header className="content-page__header contact-page__header">
            <p className="content-page__eyebrow">We are here to help</p>
            <h1 id="contact-title">Contact sickzuno</h1>
            <p className="content-page__lead">
              Tell us what is on your mind about products, shipping or an order.
            </p>
          </header>

          {showSuccess && (
            <div
              className="contact-page__success"
              role="status"
              aria-live="polite"
              tabIndex="-1"
              ref={successRef}
            >
              <strong>Thanks — your message is ready.</strong>
              <span>For a direct reply, you can also email harv@sickzuno.online.</span>
            </div>
          )}

          <div className="contact-page__layout">
            <aside className="contact-page__details" aria-labelledby="contact-details-title">
              <p className="content-page__eyebrow">Customer Care</p>
              <h2 id="contact-details-title">A helpful answer starts with the details.</h2>
              <p>
                Include the product name and, for an order question, the order
                reference. Never include payment-card details in a message.
              </p>

              <dl className="contact-page__details-list">
                <div>
                  <dt>Email</dt>
                  <dd><a href="mailto:harv@sickzuno.online">harv@sickzuno.online</a></dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>
                    <address>
                      Av. del Calvario 13, Tetexcala, 55886 Tepexpan, Méx., MX
                    </address>
                  </dd>
                </div>
                <div>
                  <dt>Support hours</dt>
                  <dd>Monday–Friday, 9am–6pm Hong Kong time</dd>
                </div>
                <div>
                  <dt>Typical topics</dt>
                  <dd>Ingredients, shipping, returns and order help</dd>
                </div>
              </dl>

              <nav className="contact-page__help-links" aria-label="Related customer care pages">
                <Link to="/shipping" onClick={scrollToTop}>Shipping guide</Link>
                <Link to="/returns" onClick={scrollToTop}>Returns guide</Link>
                <Link to="/privacy" onClick={scrollToTop}>Privacy policy</Link>
              </nav>
            </aside>

            <section className="contact-page__form-panel" aria-labelledby="write-us-title">
              <div className="contact-page__form-heading">
                <p className="content-page__eyebrow">Write Us</p>
                <h2 id="write-us-title">Jot us a note</h2>
                <p id="contact-demo-note">
                  Required fields are marked with an asterisk. Do not include card details.
                </p>
              </div>

              <form
                className="contact-page__form"
                onSubmit={handleSubmit}
                noValidate
                aria-describedby="contact-demo-note"
                aria-busy={isSubmitting}
              >
                <div className="contact-page__form-row">
                  <div className="contact-page__field">
                    <label htmlFor="firstName">
                      First name <span aria-hidden="true">*</span>
                      <span className="content-page__visually-hidden"> (required)</span>
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      autoComplete="given-name"
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={errors.firstName ? "firstName-error" : undefined}
                    />
                    {errors.firstName && (
                      <p className="contact-page__error" id="firstName-error" role="alert">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div className="contact-page__field">
                    <label htmlFor="lastName">
                      Last name <span aria-hidden="true">*</span>
                      <span className="content-page__visually-hidden"> (required)</span>
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      autoComplete="family-name"
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={errors.lastName ? "lastName-error" : undefined}
                    />
                    {errors.lastName && (
                      <p className="contact-page__error" id="lastName-error" role="alert">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="contact-page__form-row">
                  <div className="contact-page__field">
                    <label htmlFor="email">
                      Email <span aria-hidden="true">*</span>
                      <span className="content-page__visually-hidden"> (required)</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? "email-error" : undefined}
                    />
                    {errors.email && (
                      <p className="contact-page__error" id="email-error" role="alert">
                        {errors.email}
                      </p>
                    )}
                  </div>

                </div>

                <div className="contact-page__field">
                  <label htmlFor="subject">
                    Subject <span aria-hidden="true">*</span>
                    <span className="content-page__visually-hidden"> (required)</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={errors.subject ? "subject-error" : undefined}
                  >
                    <option value="">Choose a topic</option>
                    <option value="product">Product or ingredient question</option>
                    <option value="shipping">Shipping and delivery</option>
                    <option value="return">Return or replacement</option>
                    <option value="order">Order help</option>
                    <option value="other">Something else</option>
                  </select>
                  {errors.subject && (
                    <p className="contact-page__error" id="subject-error" role="alert">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div className="contact-page__field">
                  <label htmlFor="message">
                    What’s on your mind? <span aria-hidden="true">*</span>
                    <span className="content-page__visually-hidden"> (required)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="7"
                    minLength="10"
                    maxLength="1000"
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={`message-help${errors.message ? " message-error" : ""}`}
                  />
                  <div className="contact-page__field-help" id="message-help">
                    <span>At least 10 characters</span>
                    <span>{formData.message.length}/1000</span>
                  </div>
                  {errors.message && (
                    <p className="contact-page__error" id="message-error" role="alert">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button className="contact-page__submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending…" : "Submit message"}
                </button>
              </form>
            </section>
          </div>
        </article>
      </div>
    </main>
  );
}

export default Contact;
